import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import Breadcrumb from '../Breadcrumb';
import MobileHeader from '../MobileHeader';
import useIsMobile from '../../hooks/useIsMobile';
import DateTimeRangePicker from '../DateTimeRangePicker';
import './Deploy.css';

interface DeployProps {
  projectId: string;
}

interface Pipeline {
  version: string;
  name: string;
  created_at: string;
  display: string;
  config?: any;
}

interface Deployment {
  deployment_id: string;
  deployment_name: string;
  pipeline_version: string;
  created_at: string;
  uat_status: string | null;
  prod_status: string | null;
  api_url?: string;
}

interface ApiKeyResult {
  api_key_id: string;
  api_key_value: string;
  api_key_name: string;
  stage: string;
}

interface ApiKey {
  api_key_id: string;
  api_key_name: string;
  stage: string;
  created_at: string;
  created_by: string;
}

const Deploy: React.FC<DeployProps> = ({ projectId }) => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiGatewayUrls, setApiGatewayUrls] = useState({ uat_url: '', prod_url: '' });
  const [selectedPipeline, setSelectedPipeline] = useState<string>('');
  const [deploymentName, setDeploymentName] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyStage, setApiKeyStage] = useState('uat');
  const [apiKeyResult, setApiKeyResult] = useState<ApiKeyResult | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteApiKeyId, setDeleteApiKeyId] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [promoteDeployment, setPromoteDeployment] = useState<Deployment | null>(null);
  const [promoteConfirmText, setPromoteConfirmText] = useState('');
  const [showTestPopup, setShowTestPopup] = useState(false);
  const [testStage, setTestStage] = useState('uat');
  const [testPayload, setTestPayload] = useState('{}');
  const [testApiKey, setTestApiKey] = useState('');
  const [showCodeGen, setShowCodeGen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [dashboardStage, setDashboardStage] = useState('uat');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  });
  const [granularity, setGranularity] = useState('hour');
  const [metricsData, setMetricsData] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchPipelines();
    fetchDeployments();
    fetchApiKeys();
    fetchApiGatewayInfo();
    fetchMetrics();
  }, [projectId]);

  useEffect(() => {
    fetchMetrics();
  }, [dashboardStage, startDate, endDate]);

  const fetchPipelines = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions`, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const pipelineArray = Array.isArray(data.versions) ? data.versions : [];
        setPipelines(pipelineArray);
        if (pipelineArray.length > 0) {
          setSelectedPipeline(pipelineArray[0].version);
        }
      } else {
        setPipelines([]);
      }
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      setPipelines([]);
    }
    setLoading(false);
  };

  const fetchDeployments = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/deployments`, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeployments(data || []);
      }
    } catch (error) {
      console.error('Error fetching deployments:', error);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/api-keys`, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data || []);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const fetchApiGatewayInfo = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api-gateway-info`, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiGatewayUrls(data);
      }
    } catch (error) {
      console.error('Error fetching API gateway info:', error);
    }
  };

  const handleDeploy = async () => {
    if (!selectedPipeline || !deploymentName.trim()) {
      alert('Please select a pipeline and enter a deployment name');
      return;
    }

    setDeploying(true);
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/deploy`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pipeline_version: selectedPipeline,
          deployment_name: deploymentName.trim()
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setShowCreateForm(false);
        setDeploymentName('');
        fetchDeployments();
      } else {
        alert('Deployment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error deploying:', error);
      alert('Deployment failed. Please try again.');
    }
    setDeploying(false);
  };

  const handleCreateApiKey = async () => {
    if (!apiKeyName.trim()) {
      alert('Please enter an API key name');
      return;
    }

    setCreatingApiKey(true);
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-api-key`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: projectId,
          api_key_name: apiKeyName.trim(),
          stage: apiKeyStage
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setApiKeyResult(result);
        setApiKeyName('');
        fetchApiKeys();
      } else {
        alert('Failed to create API key. Please try again.');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key. Please try again.');
    }
    setCreatingApiKey(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadApiKey = (apiKey: string, name: string) => {
    const element = document.createElement('a');
    const file = new Blob([apiKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${name}-api-key.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const closeApiKeyPopup = () => {
    setShowApiKeyForm(false);
    setApiKeyResult(null);
    setApiKeyName('');
    setApiKeyStage('uat');
  };

  const handleDeleteApiKey = (apiKeyId: string) => {
    setDeleteApiKeyId(apiKeyId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteApiKey = async () => {
    if (deleteConfirmText !== 'delete') {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/api-keys`, {
        method: 'DELETE',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          api_key_id: deleteApiKeyId
        })
      });
      
      if (response.ok) {
        fetchApiKeys();
        closeDeleteConfirm();
      } else {
        alert('Failed to delete API key. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key. Please try again.');
    }
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteApiKeyId('');
    setDeleteConfirmText('');
  };

  const handlePromoteToProd = (deployment: Deployment) => {
    setPromoteDeployment(deployment);
    setShowPromoteConfirm(true);
  };

  const confirmPromoteToProd = async () => {
    if (promoteConfirmText !== 'promote' || !promoteDeployment) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/deploy`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pipeline_version: promoteDeployment.pipeline_version,
          deployment_name: promoteDeployment.deployment_name,
          stage: 'prod',
          shared_deployment_id: promoteDeployment.deployment_id
        })
      });
      
      if (response.ok) {
        fetchDeployments();
        closePromoteConfirm();
      } else {
        alert('Failed to promote to production. Please try again.');
      }
    } catch (error) {
      console.error('Error promoting to production:', error);
      alert('Failed to promote to production. Please try again.');
    }
  };

  const closePromoteConfirm = () => {
    setShowPromoteConfirm(false);
    setPromoteDeployment(null);
    setPromoteConfirmText('');
  };

  const generateCode = () => {
    const url = `${testStage === 'uat' ? apiGatewayUrls.uat_url : apiGatewayUrls.prod_url}/${projectId}/run`;
    const payload = testPayload;
    const apiKey = testApiKey;

    const codeTemplates: { [key: string]: string } = {
      javascript: `fetch('${url}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',${apiKey ? `\n    'X-API-Key': '${apiKey}',` : ''}
  },
  body: JSON.stringify(${payload})
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`,
      python: `import requests
import json

url = '${url}'
headers = {
    'Content-Type': 'application/json',${apiKey ? `\n    'X-API-Key': '${apiKey}',` : ''}
}
data = ${payload}

response = requests.post(url, headers=headers, json=data)
print(response.json())`,
      pythonhttp: `import http.client
import json
from urllib.parse import urlparse

url = '${url}'
parsed_url = urlparse(url)
conn = http.client.HTTPSConnection(parsed_url.netloc)

headers = {
    'Content-Type': 'application/json',${apiKey ? `\n    'X-API-Key': '${apiKey}',` : ''}
}
data = json.dumps(${payload})

conn.request('POST', parsed_url.path, data, headers)
response = conn.getresponse()
print(response.read().decode())
conn.close()`,
      curl: `curl -X POST '${url}' \\${apiKey ? `\n  -H 'X-API-Key: ${apiKey}' \\` : ''}
  -H 'Content-Type: application/json' \\
  -d '${payload.replace(/'/g, "'\"'\"'")}'`,
      nodejs: `const axios = require('axios');

const config = {
  method: 'post',
  url: '${url}',
  headers: {
    'Content-Type': 'application/json',${apiKey ? `\n    'X-API-Key': '${apiKey}',` : ''}
  },
  data: ${payload}
};

axios(config)
.then(response => console.log(response.data))
.catch(error => console.log(error));`,
      java: `import java.net.http.*;
import java.net.URI;

HttpClient client = HttpClient.newHttpClient();
HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("${url}"))
    .header("Content-Type", "application/json")${apiKey ? `\n    .header("X-API-Key", "${apiKey}")` : ''}
    .POST(HttpRequest.BodyPublishers.ofString("${payload.replace(/"/g, '\\"')}"))
    .build();

HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());
System.out.println(response.body());`
    };

    return codeTemplates[selectedLanguage as keyof typeof codeTemplates] || codeTemplates.javascript;
  };

  const generateSamplePayload = async () => {
    try {
      // Get active pipeline version for the selected stage
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/deployments`, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const deployments = await response.json();
        const activeDeployment = deployments.find((d: Deployment) => 
          testStage === 'uat' ? d.uat_status === 'active' : d.prod_status === 'active'
        );
        
        if (activeDeployment) {
          // Get pipeline config
          const configResponse = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions/${activeDeployment.pipeline_version}`, {
            headers: {
              'Authorization': authToken || ''
            }
          });
          
          if (configResponse.ok) {
            const configData = await configResponse.json();
            const inputSchema = configData.config.inputSchema;
            
            // Generate sample data based on schema
            const sampleData: any = {};
            for (const [key, prop] of Object.entries(inputSchema.properties || {})) {
              const property = prop as any;
              if (property.type === 'string') {
                sampleData[key] = property.description || `sample_${key}`;
              } else if (property.type === 'number') {
                sampleData[key] = 42;
              } else {
                sampleData[key] = `sample_${key}`;
              }
            }
            
            setTestPayload(JSON.stringify(sampleData, null, 2));
          }
        }
      }
    } catch (error) {
      console.error('Error generating sample payload:', error);
    }
  };

  const handleTestApi = async () => {
    try {
      const testUrl = testStage === 'uat' ? apiGatewayUrls.uat_url : apiGatewayUrls.prod_url;
      const headers: any = {
        'Content-Type': 'application/json'
      };
      
      if (testApiKey.trim()) {
        headers['X-API-Key'] = testApiKey.trim();
      }
      
      const response = await fetch(`${testUrl}/${projectId}/run`, {
        method: 'POST',
        headers,
        body: testPayload
      });
      
      const result = await response.json();
      return { status: response.status, data: result };
    } catch (error) {
      return { status: 500, data: { error: error instanceof Error ? error.message : 'Unknown error' } };
    }
  };

  const formatSingaporeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };



  const getMetricsCacheKey = () => `metrics_${projectId}_${dashboardStage}_${granularity}`;

  const getCachedMetrics = () => {
    try {
      const cached = localStorage.getItem(getMetricsCacheKey());
      return cached ? JSON.parse(cached) : { ranges: [], data: [] };
    } catch {
      return { ranges: [], data: [] };
    }
  };

  const setCachedMetrics = (cache: any) => {
    try {
      localStorage.setItem(getMetricsCacheKey(), JSON.stringify(cache));
    } catch {}
  };

  const fetchMetrics = async () => {
    const requestStart = startDate.getTime();
    const requestEnd = endDate.getTime();
    const cache = getCachedMetrics();
    
    const overlaps = cache.ranges.filter((r: any) => 
      r.start <= requestEnd && r.end >= requestStart
    );
    
    let gaps = [{ start: requestStart, end: requestEnd }];
    overlaps.forEach((overlap: any) => {
      gaps = gaps.flatMap(gap => {
        if (gap.end < overlap.start || gap.start > overlap.end) return [gap];
        const parts = [];
        if (gap.start < overlap.start) parts.push({ start: gap.start, end: overlap.start - 1 });
        if (gap.end > overlap.end) parts.push({ start: overlap.end + 1, end: gap.end });
        return parts;
      });
    });

    setMetricsLoading(true);
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      for (const gap of gaps) {
        const params = new URLSearchParams({
          stage: dashboardStage,
          start_time: new Date(gap.start).toISOString(),
          end_time: new Date(gap.end).toISOString(),
          granularity
        });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/metrics?${params}`, {
          headers: {
            'Authorization': authToken || ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          cache.data.push({ ...data, timestamp: gap.start });
          cache.ranges.push({ start: gap.start, end: gap.end });
        }
      }
      
      cache.ranges.sort((a: any, b: any) => a.start - b.start);
      const merged = [];
      for (const range of cache.ranges) {
        if (merged.length && merged[merged.length - 1].end >= range.start - 1) {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, range.end);
        } else {
          merged.push(range);
        }
      }
      cache.ranges = merged;
      
      setCachedMetrics(cache);
      
      const relevantData = cache.data.filter((d: any) => 
        d.timestamp >= requestStart && d.timestamp <= requestEnd
      );
      
      if (relevantData.length > 0) {
        const mergedMetrics: any = {
          time_series: [],
          status_codes: [],
          api_keys: []
        };
        
        relevantData.forEach((d: any) => {
          if (d.time_series) mergedMetrics.time_series.push(...d.time_series);
          if (d.status_codes) mergedMetrics.status_codes.push(...d.status_codes);
          if (d.api_keys) mergedMetrics.api_keys.push(...d.api_keys);
        });
        
        setMetricsData(mergedMetrics);
      }
      
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
    setMetricsLoading(false);
  };



  const getXAxisLabels = () => {
    if (!metricsData?.time_series?.length) return [];
    
    const start = new Date(metricsData.time_series[0].timestamp);
    const end = new Date(metricsData.time_series[metricsData.time_series.length - 1].timestamp);
    const rangeDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    const rangeHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const rangeMonths = rangeDays / 30;
    
    const labels = [];
    
    if (rangeMonths > 3) {
      // Weekly intervals
      for (let i = 0; i < 5; i++) {
        const date = new Date(start.getTime() + (i * (end.getTime() - start.getTime()) / 4));
        labels.push(date.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', month: 'short', day: 'numeric' }));
      }
    } else if (rangeDays > 7) {
      // Daily intervals
      for (let i = 0; i < 5; i++) {
        const date = new Date(start.getTime() + (i * (end.getTime() - start.getTime()) / 4));
        labels.push(date.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', month: 'short', day: 'numeric' }));
      }
    } else if (rangeHours > 1) {
      // Hourly intervals
      for (let i = 0; i < 5; i++) {
        const date = new Date(start.getTime() + (i * (end.getTime() - start.getTime()) / 4));
        labels.push(date.toLocaleString('en-SG', { timeZone: 'Asia/Singapore', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }));
      }
    } else {
      // Minute intervals
      for (let i = 0; i < 5; i++) {
        const date = new Date(start.getTime() + (i * (end.getTime() - start.getTime()) / 4));
        labels.push(date.toLocaleString('en-SG', { timeZone: 'Asia/Singapore', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }));
      }
    }
    
    return labels;
  };

  const renderTimeSeriesChart = () => {
    if (!metricsData?.time_series) return <div>No data</div>;
    
    const maxRequests = Math.max(...metricsData.time_series.map((d: any) => d.requests));
    const yLimit = Math.ceil(maxRequests * 1.2);
    const chartHeight = 250;
    
    return (
      <div style={{ height: '300px', padding: '1rem 0', position: 'relative' }}>
        {/* Y-axis */}
        <div style={{ position: 'absolute', left: '0', top: '1rem', bottom: '2rem', width: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.7rem', color: '#666' }}>
          <div>{yLimit}</div>
          <div>{Math.round(yLimit * 0.75)}</div>
          <div>{Math.round(yLimit * 0.5)}</div>
          <div>{Math.round(yLimit * 0.25)}</div>
          <div>0</div>
        </div>
        
        {/* Chart area */}
        <div style={{ marginLeft: '50px', marginBottom: '30px', height: `${chartHeight}px`, display: 'flex', alignItems: 'end', gap: '2px', position: 'relative' }}>
          {metricsData.time_series.map((point: any, index: number) => {
            const barHeight = maxRequests > 0 ? (point.requests / yLimit) * chartHeight : 0;
            return (
              <div
                key={index}
                style={{
                  flex: 1,
                  height: `${barHeight}px`,
                  backgroundColor: '#007bff',
                  borderRadius: '2px 2px 0 0',
                  minHeight: point.requests > 0 ? '2px' : '0px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                  const tooltip = document.createElement('div');
                  tooltip.className = 'chart-tooltip';
                  tooltip.style.cssText = `
                    position: absolute;
                    background: rgba(0,0,0,0.8);
                    color: white;
                    padding: 0.5rem;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    pointer-events: none;
                    z-index: 1000;
                    white-space: nowrap;
                  `;
                  const sgTime = new Date(point.timestamp).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' });
                  tooltip.textContent = `${sgTime}: ${point.requests} requests`;
                  document.body.appendChild(tooltip);
                  
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltip.style.left = `${rect.left + rect.width / 2 - tooltip.offsetWidth / 2}px`;
                  tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  const tooltip = document.querySelector('.chart-tooltip');
                  if (tooltip) document.body.removeChild(tooltip);
                }}
              />
            );
          })}
        </div>
        
        {/* X-axis */}
        <div style={{ marginLeft: '50px', height: '20px', display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#666' }}>
          {getXAxisLabels().map((label, index) => (
            <div key={index}>{label}</div>
          ))}
        </div>
      </div>
    );
  };

  const renderStatusChart = () => {
    if (!metricsData?.status_codes) return <div>No data</div>;
    
    const maxCount = Math.max(...metricsData.status_codes.map((d: any) => d.count));
    
    return (
      <div style={{ height: '200px', display: 'flex', alignItems: 'end', gap: '8px', padding: '1rem 0', overflow: 'hidden' }}>
        {metricsData.status_codes.map((status: any) => (
          <div key={status.status_code} style={{ flex: 1, textAlign: 'center' }}>
            <div
              style={{
                height: `${(status.count / maxCount) * 150}px`,
                backgroundColor: status.status_code.startsWith('2') ? '#28a745' : 
                                status.status_code.startsWith('4') ? '#ffc107' : '#dc3545',
                borderRadius: '4px 4px 0 0',
                minHeight: '2px',
                marginBottom: '4px',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
                const tooltip = document.createElement('div');
                tooltip.className = 'status-tooltip';
                tooltip.style.cssText = `
                  position: absolute;
                  background: rgba(0,0,0,0.8);
                  color: white;
                  padding: 0.5rem;
                  border-radius: 4px;
                  font-size: 0.8rem;
                  pointer-events: none;
                  z-index: 1000;
                  white-space: nowrap;
                `;
                tooltip.textContent = `Status ${status.status_code}: ${status.count} requests`;
                document.body.appendChild(tooltip);
                
                const rect = e.currentTarget.getBoundingClientRect();
                tooltip.style.left = `${rect.left + rect.width / 2 - tooltip.offsetWidth / 2}px`;
                tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                const tooltip = document.querySelector('.status-tooltip');
                if (tooltip) document.body.removeChild(tooltip);
              }}
            />
            <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{status.status_code}</div>
            <div style={{ fontSize: '0.7rem', color: '#666' }}>{status.count}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderApiKeyChart = () => {
    if (!metricsData?.api_keys) return <div>No data</div>;
    
    const maxRequests = Math.max(...metricsData.api_keys.map((d: any) => d.requests));
    
    const getStatusColor = (status: string) => {
      if (status.startsWith('2')) return '#28a745';
      if (status.startsWith('4')) return '#ffc107';
      if (status.startsWith('5')) return '#dc3545';
      return '#6c757d';
    };
    
    return (
      <div style={{ height: '200px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '1rem 0', overflow: 'hidden' }}>
        {metricsData.api_keys.map((apiKey: any) => {
          const statusBreakdown = apiKey.status_breakdown || {};
          const totalWidth = (apiKey.requests / maxRequests) * 150;
          
          return (
            <div key={apiKey.api_key_id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ fontSize: '0.7rem', minWidth: '120px', textAlign: 'right', fontFamily: 'monospace' }}>
                {apiKey.api_key_id}
              </div>
              <div 
                style={{ 
                  display: 'flex', 
                  height: '20px', 
                  minWidth: '2px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                  const tooltip = document.createElement('div');
                  tooltip.className = 'apikey-tooltip';
                  tooltip.style.cssText = `
                    position: absolute;
                    background: rgba(0,0,0,0.8);
                    color: white;
                    padding: 0.5rem;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    pointer-events: none;
                    z-index: 1000;
                    white-space: pre-line;
                    font-family: monospace;
                  `;
                  
                  const statusLines = Object.entries(statusBreakdown)
                    .map(([statusCode, statusCount]) => `- Status ${statusCode}: ${statusCount}`)
                    .join('\n');
                  
                  tooltip.textContent = `${apiKey.api_key_id}\n${statusLines}`;
                  document.body.appendChild(tooltip);
                  
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltip.style.left = `${rect.left + rect.width / 2 - tooltip.offsetWidth / 2}px`;
                  tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  const tooltip = document.querySelector('.apikey-tooltip');
                  if (tooltip) document.body.removeChild(tooltip);
                }}
              >
                {Object.entries(statusBreakdown).map(([status, count]: [string, any]) => {
                  const segmentWidth = (count / apiKey.requests) * totalWidth;
                  return (
                    <div
                      key={status}
                      style={{
                        width: `${segmentWidth}px`,
                        height: '20px',
                        backgroundColor: getStatusColor(status),
                        minWidth: segmentWidth > 0 ? '1px' : '0px'
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#666' }}>{apiKey.requests}</div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="module-content">
      <MobileHeader 
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        title="Deploy"
      />
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        { label: 'Deploy' }
      ]} />
      
      <h2>API Deployments</h2>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
        <button 
          onClick={() => setShowCreateForm(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Create Deployment
        </button>
        <button 
          onClick={() => setShowApiKeyForm(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Create API Key
        </button>
      </div>
      
      {/* Dashboard */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Environment:</label>
            <select 
              value={dashboardStage} 
              onChange={(e) => setDashboardStage(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="uat">UAT</option>
              <option value="prod">PROD</option>
            </select>
          </div>
          <DateTimeRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Granularity:</label>
            <select 
              value={granularity} 
              onChange={(e) => setGranularity(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="minute">Minute</option>
              <option value="5minute">5 Minutes</option>
              <option value="10minute">10 Minutes</option>
              <option value="hour">Hour</option>
              <option value="day">Day</option>
            </select>
          </div>
          <button 
            onClick={() => {
              localStorage.removeItem(getMetricsCacheKey());
              fetchMetrics();
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
        
        {metricsLoading ? (
          <div style={{ 
            height: '400px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}></div>
              <p>Loading metrics...</p>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', height: '400px' }}>
              <h4 style={{ margin: '0 0 1rem 0' }}>Requests Over Time</h4>
              {renderTimeSeriesChart()}
            </div>
            <div className="deploy-charts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', height: '300px', marginBottom: '2rem' }}>
              <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
                <h4 style={{ margin: '0 0 1rem 0' }}>Status Codes</h4>
                {renderStatusChart()}
              </div>
              <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
                <h4 style={{ margin: '0 0 1rem 0' }}>API Key Usage</h4>
                {renderApiKeyChart()}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Deployment Popup */}
      {showCreateForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ marginTop: 0 }}>Create New Deployment</h3>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Pipeline Version:</label>
              <select 
                value={selectedPipeline} 
                onChange={(e) => setSelectedPipeline(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              >
                {pipelines.map(pipeline => (
                  <option key={pipeline.version} value={pipeline.version}>
                    {pipeline.display}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Deployment Name:</label>
              <input 
                type="text" 
                value={deploymentName}
                onChange={(e) => setDeploymentName(e.target.value)}
                placeholder="Enter a name for this deployment"
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeploy}
                disabled={deploying || !selectedPipeline || !deploymentName.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: deploying ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: deploying ? 'not-allowed' : 'pointer'
                }}
              >
                {deploying ? 'Deploying...' : '🚀 Deploy'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create API Key Popup */}
      {showApiKeyForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw'
          }}>
            {!apiKeyResult ? (
              <>
                <h3 style={{ marginTop: 0 }}>Create API Key</h3>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>API Key Name:</label>
                  <input 
                    type="text" 
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                    placeholder="Enter a name for the API key"
                    style={{ 
                      width: '100%', 
                      padding: '0.75rem', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Stage:</label>
                  <select 
                    value={apiKeyStage} 
                    onChange={(e) => setApiKeyStage(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.75rem', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="uat">UAT</option>
                    <option value="prod">PROD</option>
                  </select>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={closeApiKeyPopup}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleCreateApiKey}
                    disabled={creatingApiKey || !apiKeyName.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: creatingApiKey ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      cursor: creatingApiKey ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creatingApiKey ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>API Key Generated</h3>
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px', 
                  padding: '1rem', 
                  marginBottom: '1rem',
                  color: '#856404'
                }}>
                  ⚠️ <strong>Warning:</strong> This API key will not be retrievable after this popup is closed. Please copy or download it now.
                </div>
                
                <div style={{ 
                  backgroundColor: '#d1ecf1', 
                  border: '1px solid #bee5eb', 
                  borderRadius: '4px', 
                  padding: '1rem', 
                  marginBottom: '1rem',
                  color: '#0c5460'
                }}>
                  ℹ️ <strong>Usage:</strong> Include this API key in the <code>X-API-Key</code> header when making requests to your deployed API.
                </div>
                
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>API Key:</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input 
                      type="text" 
                      value={apiKeyResult.api_key_value}
                      readOnly
                      style={{ 
                        flex: 1,
                        padding: '0.75rem', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        fontSize: '1rem',
                        backgroundColor: '#f8f9fa'
                      }}
                    />
                    <button 
                      onClick={() => copyToClipboard(apiKeyResult.api_key_value)}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => downloadApiKey(apiKeyResult.api_key_value, apiKeyResult.api_key_name)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    📥 Download
                  </button>
                  <button 
                    onClick={closeApiKeyPopup}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Delete API Key Confirmation Popup */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>Delete API Key</h3>
            <p style={{ marginBottom: '1rem' }}>
              This action cannot be undone. This will permanently delete the API key.
            </p>
            <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
              Please type <strong>delete</strong> to confirm:
            </p>
            
            <input 
              type="text" 
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type 'delete' to confirm"
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={closeDeleteConfirm}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteApiKey}
                disabled={deleteConfirmText !== 'delete'}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: deleteConfirmText === 'delete' ? '#dc3545' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: deleteConfirmText === 'delete' ? 'pointer' : 'not-allowed'
                }}
              >
                Delete API Key
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Promote to Prod Confirmation Popup */}
      {showPromoteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ marginTop: 0, color: '#007bff' }}>Promote to Production</h3>
            <p style={{ marginBottom: '1rem' }}>
              This will create a new production deployment with pipeline version v{promoteDeployment?.pipeline_version}.
            </p>
            <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
              Please type <strong>promote</strong> to confirm:
            </p>
            
            <input 
              type="text" 
              value={promoteConfirmText}
              onChange={(e) => setPromoteConfirmText(e.target.value)}
              placeholder="Type 'promote' to confirm"
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={closePromoteConfirm}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmPromoteToProd}
                disabled={promoteConfirmText !== 'promote'}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: promoteConfirmText === 'promote' ? '#007bff' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: promoteConfirmText === 'promote' ? 'pointer' : 'not-allowed'
                }}
              >
                Promote to Production
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Test API Popup */}
      {showTestPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="test-popup" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: showCodeGen ? '1200px' : '600px',
            maxWidth: '95vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Test API</h3>
              <button
                onClick={() => setShowCodeGen(!showCodeGen)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                {showCodeGen ? 'Hide Code' : '📝 Generate Code'}
              </button>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Environment:</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input 
                    type="radio" 
                    value="uat" 
                    checked={testStage === 'uat'} 
                    onChange={(e) => setTestStage(e.target.value)}
                  />
                  UAT
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input 
                    type="radio" 
                    value="prod" 
                    checked={testStage === 'prod'} 
                    onChange={(e) => setTestStage(e.target.value)}
                  />
                  PROD
                </label>
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Endpoint:</label>
              <code style={{ 
                display: 'block',
                padding: '0.5rem', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}>
                {testStage === 'uat' ? apiGatewayUrls.uat_url : apiGatewayUrls.prod_url}/{projectId}/run
              </code>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>API Key:</label>
              <div 
                style={{
                  position: 'relative',
                  border: '2px dashed #ddd',
                  borderRadius: '4px',
                  padding: '0.5rem',
                  backgroundColor: '#f8f9fa',
                  transition: 'border-color 0.2s ease'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#007bff';
                  e.currentTarget.style.backgroundColor = '#e3f2fd';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    const file = files[0];
                    if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const content = event.target?.result as string;
                        setTestApiKey(content.trim());
                      };
                      reader.readAsText(file);
                    }
                  }
                }}
              >
                <input 
                  type="text"
                  value={testApiKey}
                  onChange={(e) => setTestApiKey(e.target.value)}
                  placeholder="Paste API key here or drag & drop .txt file"
                  style={{ 
                    width: '100%', 
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    fontSize: '0.9rem',
                    fontFamily: 'Monaco, monospace'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  right: '0.5rem',
                  transform: 'translateY(-50%)',
                  fontSize: '0.8rem',
                  color: '#6c757d',
                  pointerEvents: 'none'
                }}>
                  📁
                </div>
              </div>
            </div>
            
            <div style={{ display: showCodeGen ? 'flex' : 'block', gap: showCodeGen ? '2rem' : '0' }}>
              <div style={{ flex: showCodeGen ? '1' : 'none', minWidth: showCodeGen ? '0' : 'auto' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>JSON Payload:</label>
                <textarea 
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  placeholder="Enter JSON payload"
                  style={{ 
                    width: '100%', 
                    height: '200px',
                    padding: '1rem', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontFamily: 'Monaco, monospace',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowTestPopup(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={async (e) => {
                    const button = e.currentTarget;
                    button.disabled = true;
                    button.innerHTML = '⏳ Testing...';
                    
                    try {
                      const result = await handleTestApi();
                      const resultDiv = document.createElement('div');
                      resultDiv.style.cssText = `
                        margin-bottom: 1rem;
                        padding: 1rem;
                        border-radius: 4px;
                        background: ${result.status === 200 ? '#d4edda' : '#f8d7da'};
                        border: 1px solid ${result.status === 200 ? '#c3e6cb' : '#f5c6cb'};
                      `;
                      resultDiv.innerHTML = `
                        <div style="font-weight: bold; margin-bottom: 0.5rem; color: ${result.status === 200 ? '#155724' : '#721c24'}">
                          Status: ${result.status}
                        </div>
                        <pre style="margin: 0; white-space: pre-wrap; font-size: 0.9rem;">${JSON.stringify(result.data, null, 2)}</pre>
                      `;
                      
                      const existingResult = document.querySelector('.test-result');
                      if (existingResult) existingResult.remove();
                      
                      resultDiv.className = 'test-result';
                      const popup = document.querySelector('.test-popup');
                      const environmentLabel = popup?.querySelector('label');
                      if (environmentLabel) {
                        popup?.insertBefore(resultDiv, environmentLabel.parentElement);
                      }
                    } finally {
                      button.disabled = false;
                      button.innerHTML = 'Test';
                    }
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Test
                </button>
              </div>
              </div>
              
              {showCodeGen && (
                <div style={{ flex: '1', borderLeft: '1px solid #ddd', paddingLeft: '2rem', minWidth: '0' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Language:</label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="javascript">JavaScript (Fetch)</option>
                    <option value="python">Python (Requests)</option>
                    <option value="pythonhttp">Python (http.client)</option>
                    <option value="curl">cURL</option>
                    <option value="nodejs">Node.js (Axios)</option>
                    <option value="java">Java (HTTP Client)</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Generated Code:</label>
                  <textarea
                    value={generateCode()}
                    readOnly
                    style={{
                      width: '100%',
                      height: '400px',
                      padding: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontFamily: 'Monaco, monospace',
                      fontSize: '0.8rem',
                      backgroundColor: '#f8f9fa',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(generateCode())}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  📋 Copy Code
                </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* API Endpoints */}
      <div className="api-endpoints-section" style={{ marginBottom: '2rem', marginTop: '3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>API Endpoints</h3>
          <button 
            onClick={async () => {
              setTestApiKey('');
              setShowCodeGen(false);
              await generateSamplePayload();
              setShowTestPopup(true);
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#218838';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#28a745';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            🧪 Test API
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <span style={{ fontWeight: 'bold', minWidth: '60px' }}>UAT:</span>
            <code style={{ flex: 1, padding: '0.5rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
              {apiGatewayUrls.uat_url}/{projectId}/run
            </code>
            <button 
              onClick={() => copyToClipboard(`${apiGatewayUrls.uat_url}/${projectId}/run`)}
              style={{
                padding: '0.5rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              📋 Copy
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <span style={{ fontWeight: 'bold', minWidth: '60px' }}>PROD:</span>
            <code style={{ flex: 1, padding: '0.5rem', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
              {apiGatewayUrls.prod_url}/{projectId}/run
            </code>
            <button 
              onClick={() => copyToClipboard(`${apiGatewayUrls.prod_url}/${projectId}/run`)}
              style={{
                padding: '0.5rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              📋 Copy
            </button>
          </div>
        </div>
      </div>
      
      {/* Deployment History Table */}
      <div className="deployment-history-section" style={{ marginTop: '3rem' }}>
        <h3>Deployment History</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Timestamp</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Deployment ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Pipeline Version</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>UAT</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>PROD</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map((deployment, index) => (
              <tr key={deployment.deployment_id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {formatSingaporeTime(deployment.created_at)}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {deployment.deployment_name || deployment.deployment_id}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: (deployment.uat_status === 'active' || deployment.prod_status === 'active') ? '#d4edda' : '#f8f9fa',
                    color: (deployment.uat_status === 'active' || deployment.prod_status === 'active') ? '#155724' : '#6c757d'
                  }}>
                    {(deployment.uat_status === 'active' || deployment.prod_status === 'active') ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  v{deployment.pipeline_version}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                  {deployment.uat_status === 'active' ? (
                    <span style={{ color: '#28a745', fontSize: '1.2rem' }}>✓</span>
                  ) : (
                    <span style={{ color: '#6c757d' }}>-</span>
                  )}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                  {deployment.prod_status === 'active' ? (
                    <span style={{ color: '#28a745', fontSize: '1.2rem' }}>✓</span>
                  ) : (
                    <span style={{ color: '#6c757d' }}>-</span>
                  )}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                  {deployment.uat_status === 'active' && !deployment.prod_status ? (
                    <button
                      onClick={() => handlePromoteToProd(deployment)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Promote to Prod
                    </button>
                  ) : (
                    <span style={{ color: '#6c757d' }}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {deployments.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>
            <p>No deployments yet. Create your first deployment!</p>
          </div>
        )}
      </div>
      
      {/* API Keys Table */}
      <div className="api-keys-section" style={{ marginTop: '3rem' }}>
        <h3>API Keys</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Created</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Stage</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>API Key ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((apiKey, index) => (
              <tr key={apiKey.api_key_id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {formatSingaporeTime(apiKey.created_at)}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {apiKey.api_key_name}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: apiKey.stage === 'prod' ? '#d4edda' : '#fff3cd',
                    color: apiKey.stage === 'prod' ? '#155724' : '#856404'
                  }}>
                    {apiKey.stage.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {apiKey.api_key_id}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <button
                    onClick={() => handleDeleteApiKey(apiKey.api_key_id)}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 6v18h18v-18h-18zm5 14c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm4-18v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.315c0 .901.73 2 1.631 2h5.712z"/>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {apiKeys.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>
            <p>No API keys yet. Create your first API key!</p>
          </div>
        )}
      </div>
      
      {pipelines.length === 0 && (
        <div className="empty-state" style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>
          <p>No pipelines found. Please create a pipeline in the Build module first.</p>
        </div>
      )}
    </div>
  );
};

export default Deploy;